import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

export const itemKey = (it) => `${it.product_id}|${it.denomination}`;

const load = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => load("zey_cart", []));
  const [buyNow, setBuyNowState] = useState(() => load("zey_buynow", null));

  useEffect(() => localStorage.setItem("zey_cart", JSON.stringify(items)), [items]);
  useEffect(() => {
    if (buyNow) localStorage.setItem("zey_buynow", JSON.stringify(buyNow));
    else localStorage.removeItem("zey_buynow");
  }, [buyNow]);

  const addItem = (item, { silent = false } = {}) => {
    setItems((prev) => {
      const k = itemKey(item);
      const existing = prev.find((p) => itemKey(p) === k);
      if (existing) {
        return prev.map((p) =>
          itemKey(p) === k ? { ...p, quantity: Math.min(10, p.quantity + item.quantity) } : p
        );
      }
      return [...prev, item];
    });
    if (!silent) toast.success("Added to cart");
  };

  const updateQty = (key, qty) =>
    setItems((prev) =>
      qty < 1
        ? prev.filter((p) => itemKey(p) !== key)
        : prev.map((p) => (itemKey(p) === key ? { ...p, quantity: Math.min(10, qty) } : p))
    );

  const removeItem = (key) => setItems((prev) => prev.filter((p) => itemKey(p) !== key));
  const removeKeys = (keys) => setItems((prev) => prev.filter((p) => !keys.includes(itemKey(p))));
  const clearCart = () => setItems([]);
  const setBuyNow = (item) => setBuyNowState(item);

  const value = useMemo(
    () => ({
      items,
      addItem,
      updateQty,
      removeItem,
      removeKeys,
      clearCart,
      buyNow,
      setBuyNow,
      count: items.reduce((n, i) => n + i.quantity, 0),
    }),
    [items, buyNow]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
